import type {
  ConnectorManifest,
  IntegrationAccessPolicy,
} from "@playbookai/api-clients";

/**
 * PB-1591 — the manifest → UI projection.
 *
 * THIS FILE IS THE TICKET. Everything the administrator console draws — the
 * product tree, the capability toggles, the resource pickers, the settings form
 * and every label — is derived here from the connector manifest, and nothing
 * else in the frontend is allowed to know what a provider is. A new connector
 * ships and this code renders it, unchanged.
 *
 * Kept pure and free of React on purpose. The rule "no provider name in any
 * component, string table, icon map or conditional" is only credible if it can
 * be tested without rendering anything, and the fictional-provider test asserts
 * against these functions directly.
 *
 * If something the admin UI needs cannot be expressed by a manifest, the answer
 * is a kernel port (EI-09), never a special case here — see the stop condition
 * in the brief and ADR-0042.
 */

export type SettingsControl =
  | "text"
  | "number"
  | "boolean"
  | "choice"
  | "secret";

export interface SettingsFieldModel {
  readonly key: string;
  readonly label: string;
  readonly description?: string;
  readonly control: SettingsControl;
  readonly required: boolean;
  /** Present only for `choice`. */
  readonly options?: readonly string[];
  /** Carried through so the form can check the value before submitting. */
  readonly format?: "uri";
}

export interface CapabilityModel {
  readonly key: string;
  readonly label: string;
  readonly kind: "read" | "write";
  readonly requiresApproval: boolean;
  readonly resourceType: string;
  /** Human label for {@link resourceType}, so the editor need not look it up. */
  readonly resourceTypeLabel: string;
  /** Input fields an administrator may restrict (PB-4734). */
  readonly constrainableInputs: readonly string[];
}

export interface ResourceTypeModel {
  readonly key: string;
  readonly label: string;
  readonly selectorLabel: string;
  readonly parentResourceType: string | null;
  /** Nesting depth, so a three-level provider indents correctly. */
  readonly depth: number;
}

export interface ProductModel {
  readonly key: string;
  readonly label: string;
  readonly capabilities: readonly CapabilityModel[];
  readonly resourceTypes: readonly ResourceTypeModel[];
}

/**
 * The settings form.
 *
 * A property the manifest marks `format: "secret"` becomes a `secret` control,
 * which `ManifestSettingsForm` then declines to render at all: since PB-4734 no
 * route accepts a secret or a reference to one from a tenant administrator, so
 * the only honest thing to show is nothing. The classification stays HERE — a
 * connector may legitimately declare a secret setting, and the decision not to
 * collect it belongs to the core, not to the manifest.
 *
 * A property marked `deprecated` is dropped outright. It stays declared in the
 * manifest so instances that already carry the key still validate, but the
 * value means nothing now and offering a box for it would say otherwise.
 */
export function settingsFields(
  manifest: ConnectorManifest,
): SettingsFieldModel[] {
  const required = new Set(manifest.settingsSchema.required ?? []);
  return Object.entries(manifest.settingsSchema.properties)
    .filter(([, property]) => property.deprecated !== true)
    .map(([key, property]) => ({
      key,
      label: property.title,
      ...(property.description === undefined
        ? {}
        : { description: property.description }),
      control: controlFor(property),
      required: required.has(key),
      ...(property.enum === undefined ? {} : { options: property.enum }),
      ...(property.format === "uri" ? { format: "uri" as const } : {}),
    }));
}

function controlFor(property: {
  type: "string" | "number" | "boolean";
  format?: "uri" | "email" | "secret" | undefined;
  enum?: readonly string[] | undefined;
}): SettingsControl {
  if (property.format === "secret") return "secret";
  if (property.enum !== undefined) return "choice";
  if (property.type === "boolean") return "boolean";
  if (property.type === "number") return "number";
  return "text";
}

/**
 * The product tree.
 *
 * Resource types are ordered parents-before-children and carry a computed
 * depth, because a manifest may declare any number of levels — the fake
 * connector has three where the first real provider has two, and a renderer
 * that assumed a fixed nesting would be the first thing to break on connector
 * number four.
 */
export function productModels(manifest: ConnectorManifest): ProductModel[] {
  return manifest.products.map((product) => ({
    key: product.key,
    label: product.displayName,
    capabilities: product.capabilities.map((capability) => ({
      key: capability.key,
      label: capability.displayName,
      kind: capability.kind,
      requiresApproval: capability.requiresApproval,
      resourceType: capability.resourceType,
      resourceTypeLabel:
        product.resourceTypes.find((r) => r.key === capability.resourceType)
          ?.displayName ?? capability.resourceType,
      // Older stored manifests pre-date this optional declaration. Treat its
      // absence as the schema default so their administration pages still open.
      constrainableInputs: capability.constrainableInputs ?? [],
    })),
    resourceTypes: orderByHierarchy(product.resourceTypes),
  }));
}

function orderByHierarchy(
  resourceTypes: ConnectorManifest["products"][number]["resourceTypes"],
): ResourceTypeModel[] {
  const ordered: ResourceTypeModel[] = [];
  const emit = (parent: string | null, depth: number): void => {
    for (const type of resourceTypes) {
      if (type.parentResourceType !== parent) continue;
      ordered.push({
        key: type.key,
        label: type.displayName,
        selectorLabel: type.selectorLabel,
        parentResourceType: type.parentResourceType,
        depth,
      });
      emit(type.key, depth + 1);
    }
  };
  emit(null, 0);
  // A manifest whose hierarchy is cyclic or points outside the product cannot
  // reach the kernel's validator, but a defensive tail keeps an unreachable
  // type visible rather than silently dropping it from the console.
  for (const type of resourceTypes) {
    if (ordered.some((o) => o.key === type.key)) continue;
    ordered.push({
      key: type.key,
      label: type.displayName,
      selectorLabel: type.selectorLabel,
      parentResourceType: type.parentResourceType,
      depth: 0,
    });
  }
  return ordered;
}

/**
 * Toggle one capability, keeping the policy internally consistent.
 *
 * Enabling a capability also enables its product, and disabling the last
 * capability of a product drops the product. That is not convenience: the
 * server rejects a capability whose product is not enabled, so a console that
 * let the two drift would produce a policy the administrator cannot save and
 * would have to be told about in an error message instead of in the UI.
 *
 * The product is derived from the capability KEY's first segment, which the
 * closed `<product>.<object>.<verb>` grammar guarantees and the kernel's
 * manifest validator enforces. No lookup table, no provider knowledge.
 */
export function toggleCapability(
  policy: IntegrationAccessPolicy,
  capabilityKey: string,
  enabled: boolean,
): IntegrationAccessPolicy {
  const product = capabilityKey.split(".")[0];
  const capabilities = new Set(policy.capabilities ?? []);
  const products = new Set(policy.products ?? []);

  if (enabled) {
    capabilities.add(capabilityKey);
    if (product !== undefined) products.add(product);
  } else {
    capabilities.delete(capabilityKey);
    const productStillUsed = [...capabilities].some(
      (key) => key.split(".")[0] === product,
    );
    if (!productStillUsed && product !== undefined) products.delete(product);
  }

  return {
    ...policy,
    products: [...products].sort(),
    capabilities: [...capabilities].sort(),
  };
}

/**
 * Toggle a whole product. Switching one off removes its capabilities too —
 * leaving them behind would be a policy the server refuses.
 */
export function toggleProduct(
  policy: IntegrationAccessPolicy,
  product: ProductModel,
  enabled: boolean,
): IntegrationAccessPolicy {
  const products = new Set(policy.products ?? []);
  const capabilities = new Set(policy.capabilities ?? []);

  if (enabled) {
    products.add(product.key);
  } else {
    products.delete(product.key);
    for (const capability of product.capabilities) {
      capabilities.delete(capability.key);
    }
    // Resource restrictions for this product's types become unreachable too.
    const owned = new Set(product.resourceTypes.map((r) => r.key));
    const resources = Object.fromEntries(
      Object.entries(policy.resources ?? {}).filter(([key]) => !owned.has(key)),
    );
    return {
      ...policy,
      products: [...products].sort(),
      capabilities: [...capabilities].sort(),
      resources,
    };
  }

  return {
    ...policy,
    products: [...products].sort(),
    capabilities: [...capabilities].sort(),
  };
}

/**
 * Set the allowlist for one resource type from the administrator's free-text
 * input.
 *
 * STALE NOTE CORRECTED (PB-4734). This used to say a live picker was blocked on
 * connections arriving in EI-04. They arrived, and PB-4734 built the picker —
 * see `PolicyAllowlistPicker`. This helper survives because DIRECT ENTRY
 * survives: the policy API keeps accepting ids, automation and recovery must
 * not require a browser, and an administrator restoring a configuration should
 * not have to click through twenty checkboxes.
 *
 * An EMPTY list is stored as an absent key rather than `[]`, because the two
 * mean opposite things: no key means "no restriction on this resource type",
 * while `[]` would mean "no resource of this type is permitted" and would
 * silently disable everything the administrator just enabled.
 */
export function setResourceAllowlist(
  policy: IntegrationAccessPolicy,
  resourceType: string,
  raw: string,
): IntegrationAccessPolicy {
  // PARSES, then delegates. The id-list form below is the primitive and this is
  // the text adapter, not the other way round: the picker already holds ids,
  // and routing them back out through a join and back in through this splitter
  // would put every provider id through a round trip that assumes no id
  // contains a newline or a comma. Provider ids are opaque, so that is not an
  // assumption this file gets to make.
  const ids = raw
    .split(/[\n,]/)
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  // A blank textarea is "stop restricting", not "permit nothing": the text
  // route cannot tell the two apart, and clearing a field reads as removal
  // everywhere else in this form. Deny-all is authored in the PICKER, where
  // unticking the last value is an explicit act on a named option.
  return setResourceAllowlistIds(
    policy,
    resourceType,
    ids.length === 0 ? undefined : ids,
  );
}

/**
 * The allowlist write from ids (PB-4734) — and the PRIMITIVE of the pair.
 *
 * Holds the absent-versus-empty rule in one place, and `undefined` versus `[]`
 * is now the distinction: no key means "no restriction on this type", `[]`
 * means "nothing of this type is permitted", and the two are OPPOSITE. This
 * used to delete the key for an empty list, which turned "untick the last
 * allowed value" into "permit everything" — the exact conflation the policy
 * model's own docblock warns has shipped as bugs in both directions. Removal is
 * the explicit `undefined`; `[]` persists as fail-closed deny-all.
 */
export function setResourceAllowlistIds(
  policy: IntegrationAccessPolicy,
  resourceType: string,
  ids: readonly string[] | undefined,
): IntegrationAccessPolicy {
  const resources: Record<string, readonly string[]> = {
    ...(policy.resources ?? {}),
  };
  if (ids === undefined) delete resources[resourceType];
  else resources[resourceType] = [...new Set(ids)];

  return { ...policy, resources };
}

/**
 * Reads the allowed ids for one resource type. `undefined` is "no restriction"
 * and `[]` is "nothing permitted" — coalescing the two loses the distinction
 * the write above exists to keep.
 */
export function resourceAllowlist(
  policy: IntegrationAccessPolicy,
  resourceType: string,
): readonly string[] | undefined {
  return policy.resources?.[resourceType];
}

/** Provider resource ids are opaque, but they are never whitespace or quotes. */
const RESOURCE_ID = /^[A-Za-z0-9._:/~-]{1,200}$/;

export function invalidResourceIds(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((value) => value.trim())
    .filter((value) => value.length > 0 && !RESOURCE_ID.test(value));
}

/** Reads the value the console should show for one resource type. */
export function resourceAllowlistText(
  policy: IntegrationAccessPolicy,
  resourceType: string,
): string {
  return (policy.resources?.[resourceType] ?? []).join(", ");
}

export const isCapabilityEnabled = (
  policy: IntegrationAccessPolicy,
  capabilityKey: string,
): boolean => (policy.capabilities ?? []).includes(capabilityKey);

export const isProductEnabled = (
  policy: IntegrationAccessPolicy,
  productKey: string,
): boolean => (policy.products ?? []).includes(productKey);

/**
 * PB-4734 (G9) — the `capabilityInputs` half of a policy.
 *
 * Same absent-versus-empty rule as `resources`, and it matters more here
 * because the two readings are opposite: no entry permits every value, an empty
 * array permits none. Both are legal, so the write has to distinguish them.
 */
export function capabilityInputAllowlist(
  policy: IntegrationAccessPolicy,
  capabilityKey: string,
  field: string,
): readonly string[] | undefined {
  return policy.capabilityInputs?.[capabilityKey]?.[field];
}

export function setCapabilityInputAllowlist(
  policy: IntegrationAccessPolicy,
  capabilityKey: string,
  field: string,
  ids: readonly string[] | undefined,
): IntegrationAccessPolicy {
  const all: Record<string, Record<string, readonly string[]>> = {};
  for (const [key, fields] of Object.entries(policy.capabilityInputs ?? {})) {
    all[key] = { ...fields };
  }
  const forCapability = { ...(all[capabilityKey] ?? {}) };

  // `undefined` deletes the KEY; `[]` persists as deny-all. The old rule
  // deleted on empty, which silently turned "untick the last allowed value"
  // into "permit every value" — and its stated escape hatch was false: the
  // textarea route also mapped blank to empty, so deny-all could not actually
  // be authored anywhere. Now unticking everything authors the fail-closed
  // reading, and removal is its own explicit act.
  if (ids === undefined) delete forCapability[field];
  else forCapability[field] = [...new Set(ids)];

  if (Object.keys(forCapability).length === 0) delete all[capabilityKey];
  else all[capabilityKey] = forCapability;

  return { ...policy, capabilityInputs: all };
}

/**
 * Which resource to discover a capability's form against.
 *
 * The form is per-resource — a capability's available values depend on where it
 * would land — so listing needs one to ask about. The first ALLOWED resource is
 * the honest choice: it is one the administrator has already committed to, and
 * an id they cannot see would fail the call anyway.
 *
 * Null when nothing is allowlisted yet, which the editor renders as the
 * dependency it is rather than as an empty list.
 */
export function probeResourceFor(
  policy: IntegrationAccessPolicy,
  capability: { readonly resourceType: string },
): string | null {
  return policy.resources?.[capability.resourceType]?.[0] ?? null;
}

/**
 * What is wrong with this value, if anything — checked before the request.
 *
 * A CONVENIENCE, not the gate. `settings-policy.ts` on the server is
 * authoritative and always runs; this exists so an administrator learns about a
 * malformed URL while their cursor is still in the field, instead of filling
 * the whole form, pressing Create, and reading a sentence at the bottom about a
 * field they have scrolled past.
 *
 * The URL rule MIRRORS the server's `isFetchableHttpUrl` exactly — parses as a
 * URL, `http:` or `https:`, non-empty host. Deliberately mirrored rather than
 * approximated: a client check stricter than the server rejects values that
 * would have worked, and a looser one promises a save that then fails, which is
 * worse than no check at all.
 *
 * What it does NOT reproduce is the server's pasted-credential heuristic. That
 * is a security net the server owns, and it deliberately takes precedence over
 * the URL message there — "you pasted a credential" is the correction that
 * helps, and a client guessing at it would risk showing the wrong one. So a
 * pasted token still reaches the server and still comes back with the server's
 * own wording.
 *
 * Manifest-driven throughout: the rule comes from `format: "uri"` in the
 * schema, so a connector shipping next quarter gets the same check with no
 * change here, and no field name is known to this file.
 */
export function settingsFieldError(
  field: SettingsFieldModel,
  value: unknown,
): string | undefined {
  const text = typeof value === "string" ? value.trim() : "";
  /* "Missing" is absence, not falsiness. Collapsing every non-string to `""`
     made a required NUMBER field permanently invalid — `0` reads as unset, and
     so did `25`. It never surfaced because only the text control was wired to
     show an error, so the bug was one fix away from being visible. */
  const missing =
    value === undefined ||
    value === null ||
    (typeof value === "string" && text === "");

  if (field.required && missing && field.control !== "boolean") {
    return `${field.label} is required.`;
  }
  /* MIRRORS THE SERVER'S CONDITION, empty string included. This guarded with
     `text !== ""`, which the server does not: its rule is
     `typeof value === "string" && !isFetchableHttpUrl(value)`, and `""` is a
     string that fails it. So an OPTIONAL uri field typed into and then cleared
     holds `""` — the text control publishes the raw string — the client said
     nothing, and the save was rejected by the server. That is the outcome this
     function exists to prevent.

     A field NEVER TOUCHED still goes unchecked on both sides, because it holds
     `undefined` rather than `""`. Optional stays optional; what is checked is a
     value the administrator actually put there and then emptied. */
  if (
    field.format === "uri" &&
    typeof value === "string" &&
    !isFetchableHttpUrl(text)
  ) {
    return "Enter a full web address starting with http:// or https://";
  }
  return undefined;
}

/** Mirrors `isFetchableHttpUrl` in the server's settings policy. */
function isFetchableHttpUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  return (
    (url.protocol === "http:" || url.protocol === "https:") && url.host !== ""
  );
}
